import {alpha} from '@mui/material';
import {amber} from '@mui/material/colors';
import {
  FooterType,
  HeaderType,
  LayoutDirection,
  LayoutType,
  MenuStyle,
  NavStyle,
  RouteTransition,
  ThemeMode,
  ThemeStyle,
} from 'shared/constants/AppEnums';

// --- Yaman Refined Colors ---
const YAMAN_GOLD = '#D4AF37';
const YAMAN_GOLD_LIGHT = '#E5C76B';
const YAMAN_GOLD_DARK = '#B8962E';
const YAMAN_ONYX = '#1A1A1B';
const YAMAN_PEARL_WHITE = '#FDFDFD';
const YAMAN_PEARL_BG = '#F4F4F2'; // Slightly warmer/surgical grey-white
const YAMAN_SUCCESS = '#008C45';
const YAMAN_ERROR = '#d32f2f';
const PURE_WHITE = '#FFFFFF';
const CONTEXT_MENU_SHADOW =
  '0px 5px 5px -3px rgba(0,0,0,0.2), 0px 8px 10px 1px rgba(0,0,0,0.14), 0px 3px 14px 2px rgba(0,0,0,0.12)';

export const YSThemeStyleRadius = {
  MODERN: 8, // Sleek, contemporary
  STANDARD: 4, // Surgical precision
  SHARP: 2, // For components
};

export const YSTextLight = {
  primary: YAMAN_ONYX, // Onyx for strong readability
  secondary: 'rgb(107, 114, 128)',
  disabled: 'rgb(149, 156, 169)',
};

export const YSTextDark = {
  primary: PURE_WHITE,
  secondary: 'rgb(229, 231, 235)',
  disabled: 'rgb(156, 163, 175)',
};

export const YSBorderState = {
  default: alpha(YAMAN_ONYX, 0.4),
  hover: YAMAN_ONYX,
  focus: YAMAN_GOLD,
  disabled: alpha(YAMAN_ONYX, 0.15),
};

export const YSIconState = {
  default: alpha(YAMAN_ONYX, 0.8),
  hover: YAMAN_ONYX,
  focus: YAMAN_GOLD,
  disabled: alpha(YAMAN_ONYX, 0.15),
};

export const YSLabelState = {
  default: alpha(YAMAN_ONYX, 0.8),
  focus: YAMAN_ONYX,
  disabled: alpha(YAMAN_ONYX, 0.3), // Buttons only
};

export const YSBackgroundState = {
  default: PURE_WHITE,
  hover: alpha(YAMAN_ONYX, 0.05),
  focus: alpha(YAMAN_GOLD, 0.08),
  focusedHover: alpha(YAMAN_GOLD, 0.12),
};

const selectionControlStyle = {
  styleOverrides: {
    root: {
      fontSize: 20,
      color: YSIconState.default,
      transition: 'all 200ms cubic-bezier(0.4, 0, 0.2, 1)',
      '&:hover': {
        color: YSIconState.hover,
        backgroundColor: YSBackgroundState.hover,
      },
      '&.Mui-checked': {
        color: YSIconState.focus,
      },
      '&.Mui-disabled': {
        color: YSIconState.disabled,
      },
    },
  },
};

export const iconStyle = {
  styleOverrides: {
    root: {
      '& .MuiSvgIcon-root': {
        fontSize: 20,
        color: YSIconState.default,
        '.Mui-focused &': {
          color: YSIconState.focus,
        },
        '&:hover': {
          color: YSIconState.hover,
          backgroundColor: YSBackgroundState.hover,
        },
        '&.Mui-disabled': {
          color: YSIconState.disabled,
        },
      },
    },
  },
};

// Keeping your Light background but refining the Pearl tone
export const YSBackgroundLight = {
  paper: YAMAN_PEARL_WHITE,
  default: YAMAN_PEARL_BG,
};

const cardRadius = YSThemeStyleRadius.STANDARD;

const tag = Object.freeze({
  text: PURE_WHITE,
  blue: '#005CB9',
  purple: '#6041A4',
  green: YAMAN_SUCCESS,
  red: YAMAN_ERROR,
  yellow: YAMAN_GOLD,
  grey: '#94A3B8',
  slate: '#475569',
  navy: '#312E81',
});

export const YSTheme = {
  theme: {
    spacing: 4,
    cardRadius: cardRadius,
    sharpRadius: YSThemeStyleRadius.SHARP,
    direction: LayoutDirection.LTR,
    palette: {
      tag: tag,
      contextMenuShadow: CONTEXT_MENU_SHADOW,
      mode: ThemeMode.LIGHT,
      background: YSBackgroundLight,
      primary: {
        main: YAMAN_GOLD, // The Gold "Ignition" Color
        contrastText: PURE_WHITE, // Use White for better "Surgical" legibility on gold
        light: YAMAN_GOLD_LIGHT, // For subtle highlights/active indicators
        dark: YAMAN_GOLD_DARK, // The "Deep Gold" for hovers
      },
      secondary: {
        main: YAMAN_ONYX, // The Onyx Structural Color
        contrastText: PURE_WHITE,
      },
      success: {
        main: YAMAN_SUCCESS,
        light: '#D9F5E5',
        dark: '#006632',
        contrastText: PURE_WHITE,
      },
      warning: {
        main: amber[700],
        light: '#FFECDC',
        dark: '#c66300',
        contrastText: 'rgba(0, 0, 0, 0.87)',
      },
      error: {
        main: YAMAN_ERROR, // A sophisticated surgical red
        light: '#FEEBEB',
        dark: '#9a2323', // Deep red for hover states
        contrastText: PURE_WHITE,
      },
      text: YSTextLight,
      gray: {
        // Keeping your grey scale for utility, but anchoring it to Onyx
        50: '#fafafa',
        100: '#F5F6FA',
        200: '#edf2f7',
        300: '#E0E0E0',
        400: '#c5c6cb',
        500: '#A8A8A8',
        600: '#666666',
        700: '#4a5568',
        800: '#201e21',
        900: YAMAN_ONYX,
        A100: '#d5d5d5',
        A200: '#aaaaaa',
        A400: '#303030',
        A700: '#616161',
      },
    },
    status: {
      danger: 'orange',
    },
    divider: YSBorderState.default,
    typography: {
      fontFamily: ['Inter', 'sans-serif'].join(','),
      fontSize: 14,
      fontWeight: 400,
      h1: {
        fontSize: 24,
        fontWeight: 700,
        textTransform: 'uppercase',
        letterSpacing: '1.5px',
        color: YAMAN_ONYX,
      },
      h2: {fontSize: 20, fontWeight: 600},
      h3: {fontSize: 18, fontWeight: 500},
      h4: {fontSize: 16, fontWeight: 500},
      h5: {fontSize: 14, fontWeight: 500},
      h6: {fontSize: 12, fontWeight: 500},
    },
    components: {
      MuiCardLg: {
        styleOverrides: {
          root: {
            // We override the base logic to force the "Machined" look
            // For Large Cards, we'll use MODERN (8px) to keep it sleek but distinct
            borderRadius: YSThemeStyleRadius.MODERN,
          },
        },
      },
      MuiCard: {
        styleOverrides: {
          root: {
            borderRadius: YSThemeStyleRadius.STANDARD,
            boxShadow: '0px 4px 20px rgba(0, 0, 0, 0.05)', // Softer, more "expensive" shadow
          },
        },
      },
      MuiButton: {
        styleOverrides: {
          root: {
            borderRadius: YSThemeStyleRadius.SHARP,
            textTransform: 'uppercase',
            fontWeight: 700,
            letterSpacing: '1.2px',
            padding: '10px 24px',
            fontSize: 12,
            height: 36,
            transition: 'all 0.2s ease-in-out',

            '&.Mui-disabled': {
              borderColor: YSIconState.disabled,
              color: YSLabelState.disabled,
              // backgroundColor: 'transparent',
            },
          },
          containedPrimary: {
            backgroundColor: YAMAN_GOLD,
            color: PURE_WHITE,
            boxShadow: 'none',
            '&:hover': {
              backgroundColor: YAMAN_GOLD_DARK, // Deep Gold hover
              boxShadow: '0px 4px 10px rgba(212, 175, 55, 0.2)',
            },
          },
          outlinedError: {
            borderColor: YAMAN_ERROR,
            color: YAMAN_ERROR,
            '&:hover': {
              backgroundColor: 'rgba(211, 47, 47, 0.04)',
              color: YAMAN_ERROR,
              borderColor: '#9a2323',
            },
          },
          outlined: {
            borderColor: YSIconState.default,
            color: YSLabelState.default,
            backgroundColor: YSBackgroundState.default,
            '&:hover': {
              borderColor: YSIconState.hover,
              color: YSLabelState.focus,
              backgroundColor: YSBackgroundState.hover,
            },
          },
        },
      },
      MuiIconButton: {
        styleOverrides: {
          root: {
            fontSize: 20,
            color: YSIconState.default,
            transition: 'all 0.3s ease',
            '&:hover': {
              color: YSIconState.hover,
              backgroundColor: YSBackgroundState.hover,
            },
            '&.Mui-disabled': {
              color: YSIconState.disabled,
            },
            '& .MuiSvgIcon-root': {
              fontSize: 'inherit',
            },
          },
        },
      },

      // Dialog
      MuiDialog: {
        styleOverrides: {
          paper: {
            borderRadius: YSThemeStyleRadius.SHARP, // Unifies with button edges
            border: '1px solid rgba(0, 0, 0, 0.08)', // Adds technical definition
            padding: '8px', // Internal gutter for the component
            margin: '8px',
          },
        },
      },
      MuiDialogTitle: {
        styleOverrides: {
          root: {
            padding: '24px 24px 12px 24px', // Standardized executive padding
            '& .MuiTypography-root': {
              fontSize: 16,
              fontWeight: 700, // Matches "NEW SHIPMENT" weight
              color: YSLabelState.default,
              letterSpacing: '0.02em',
              textTransform: 'uppercase',
            },
          },
        },
      },
      MuiDialogContent: {
        styleOverrides: {
          root: {
            // padding: '8px 24px 24px 24px',
            color: YSLabelState.focus, // Softer for the message text
            fontSize: 13, // Matches your table/search scale
            // letterSpacing: '0.1em',
            lineHeight: 1.5,
          },
        },
      },
      MuiDialogActions: {
        styleOverrides: {
          root: {
            padding: '0 24px 24px 24px',
            justifyContent: 'flex-end', // Left-to-right flow
            gap: '12px', // Surgical button spacing
            letterSpacing: '1.2px',
          },
        },
      },

      // Form Component Error Text
      MuiFormHelperText: {
        styleOverrides: {
          root: {
            fontSize: 12,
            marginTop: 2,
            marginLeft: 4,
            '&.Mui-error': {
              color: YAMAN_ERROR,
            },
          },
        },
      },

      MuiFormControl: {
        styleOverrides: {
          root: {
            marginBottom: 0,
          },
        },
      },

      // Form Fields and Labels
      MuiOutlinedInput: {
        styleOverrides: {
          root: {
            height: 45, // Reduced from default ~56px for a sleeker look
            borderRadius: YSThemeStyleRadius.SHARP,
            backgroundColor: YSBackgroundState.default,
            transition: 'border-color 0.2s cubic-bezier(0.4, 0, 0.2, 1)',
            '& .MuiOutlinedInput-notchedOutline': {
              borderColor: YSBorderState.default,
            },
            '&:hover .MuiOutlinedInput-notchedOutline': {
              borderColor: YSBorderState.hover,
            },
            '&.Mui-focused .MuiOutlinedInput-notchedOutline': {
              borderColor: YSBorderState.focus,
              borderWidth: 1,
            },
            '&.Mui-disabled & .MuiOutlinedInput-notchedOutline': {
              borderColor: YSBorderState.disabled,
            },
          },
          input: {
            fontSize: 14, // Slightly smaller for professional density
            color: YSTextLight.primary,
            '&.Mui-disabled': {
              WebkitTextFillColor: YSTextLight.disabled,
            },
            display: 'flex',
            padding: '0 12px', // Remove vertical padding
            height: '40px', // Let height handle the centering
            alignItems: 'center',
          },
        },
      },
      MuiInputLabel: {
        styleOverrides: {
          root: {
            fontSize: 13,
            fontWeight: 500,
            letterSpacing: '0.1em',
            lineHeight: 1,
            color: YSLabelState.default,
            transform: 'translate(14px, 23px) scale(1)',
            transition: 'all 200ms cubic-bezier(0.0, 0, 0.2, 1) 0ms',
            '&.Mui-focused': {
              color: YSLabelState.focus,
            },
          },
          shrink: {
            backgroundColor: PURE_WHITE,
            padding: '0 8px !important',
            zIndex: 2,
            marginLeft: '-4px',
            transform: 'translate(12px, 2px) scale(0.8) !important',
          },
        },
      },
      MuiTextField: {
        defaultProps: {
          size: 'small', // This is the first step to smaller fields
          variant: 'outlined',
        },
      },
      // Field Labels
      MuiFormLabel: {
        styleOverrides: {
          root: {
            marginTop: '-7px',
            lineHeight: 1,
            fontSize: 12,
            letterSpacing: '0.1em',
            color: YSLabelState.default,
            '&.Mui-focused': {
              color: YSLabelState.focus,
            },
            '&.Mui-error': {
              color: YAMAN_ERROR,
            },
          },
        },
      },

      // Star Rating
      MuiRating: {
        styleOverrides: {
          root: {
            fontSize: 20,
            color: YSIconState.focus,
            transition: 'transform 0.2s ease-in-out',
            '&.Mui-disabled': {
              color: YSIconState.disabled,
              opacity: 1, // Ensure MUI doesn't add extra default opacity
            },
          },
          iconEmpty: {
            color: YSIconState.default,
          },
          decimal: {
            '&:hover': {
              transform: 'scale(1.1)', // Subtle luxury interaction
            },
          },
        },
      },

      // Select Dropdown
      MuiSelect: {
        defaultProps: {
          MenuProps: {
            anchorOrigin: {
              vertical: 'bottom',
              horizontal: 'left',
            },
            transformOrigin: {
              vertical: 'top',
              horizontal: 'left',
            },
          },
        },
        styleOverrides: {
          root: {
            borderRadius: YSThemeStyleRadius.SHARP,
          },
          icon: {
            fontSize: 20,
            right: 12,
            color: YSIconState.default,
            '&.MuiSelect-iconOpen': {
              color: YSIconState.focus,
            },
            '&:hover': {
              color: YSIconState.hover,
            },
            '&.Mui-disabled': {
              color: YSIconState.disabled,
            },
          },
        },
      },
      MuiMenu: {
        styleOverrides: {
          paper: {
            borderRadius: YSThemeStyleRadius.SHARP,
            marginTop: 12, // The modern "float" gap
            borderWidth: 1,
            borderColor: YSBorderState.default,
            boxShadow: CONTEXT_MENU_SHADOW,
            maxHeight: '320px !important',
            overflowY: 'auto',
            '&::-webkit-scrollbar': {
              width: '6px',
            },
            '&::-webkit-scrollbar-track': {
              backgroundColor: 'transparent',
            },
            '&::-webkit-scrollbar-thumb': {
              backgroundColor: alpha(YAMAN_ONYX, 0.1),
              borderRadius: '10px',
              '&:hover': {
                backgroundColor: YSBorderState.hover, // alpha(YAMAN_GOLD, 0.4), // "Ignites" on hover
              },
            },
            '& .MuiList-root': {
              padding: 0, // Makes the list look "machined"
            },
          },
        },
      },
      MuiMenuItem: {
        styleOverrides: {
          root: {
            fontSize: 13,
            padding: '6px 10px',
            color: YSLabelState.default,
            transition: 'background-color 0.15s ease',
            '&:hover': {
              backgroundColor: YSBackgroundState.hover,
            },
            '&.Mui-selected': {
              backgroundColor: YSBackgroundState.focus,
              fontWeight: 600, // Makes the selection feel "set"
              '&:hover': {
                backgroundColor: YSBackgroundState.focusedHover,
              },
            },
          },
        },
      },

      // Lables of Switch, Checkbox, Radio options
      MuiFormControlLabel: {
        styleOverrides: {
          label: {
            fontSize: 13, // Dropping to 13px for technical density
            fontWeight: 500, // Keeps it sharp and "Surgical"
            color: YSLabelState.default,
            letterSpacing: '0.02em',
          },
        },
      },
      MuiCheckbox: selectionControlStyle,
      MuiRadio: selectionControlStyle,
      MuiSwitch: {
        styleOverrides: {
          switchBase: {
            color: YSBackgroundState.default,
            '&.Mui-checked': {
              color: YSBackgroundState.default,
              '& + .MuiSwitch-track': {
                opacity: 1,
                backgroundColor: YAMAN_GOLD,
              },
            },
          },
          track: {
            borderRadius: YSThemeStyleRadius.SHARP,
            backgroundColor: alpha(YSBorderState.default, 0.3),
            opacity: 1, // Crucial: ensures a solid, machined feel
            transition: 'background-color 0.2s ease',
          },
          thumb: {
            boxShadow: '0px 2px 4px rgba(0,0,0,0.2)', // Adds physical depth
            borderRadius: YSThemeStyleRadius.SHARP,
          },
        },
      },

      // Toggle Button
      MuiToggleButtonGroup: {
        styleOverrides: {
          root: {
            borderRadius: YSThemeStyleRadius.SHARP,
            backgroundColor: YSBackgroundState.default,
            border: `1px solid ${YSBorderState.default}`,
            color: YAMAN_ONYX,
            marginTop: '4px',
            '& .MuiToggleButton-root.Mui-selected': {
              backgroundColor: YSBackgroundState.focus,
              fontWeight: 600,
              '&:hover': {
                backgroundColor: YSBackgroundState.focusedHover,
              },
            },
          },
        },
      },
      MuiToggleButton: {
        styleOverrides: {
          root: {
            border: 'none',
            borderRadius: YSThemeStyleRadius.SHARP,
            padding: '4px 16px',
            color: YSLabelState.default,
            fontSize: 13,
            fontWeight: 500,
            letterSpacing: '0.05em',
            textTransform: 'uppercase',
            transition: 'all 0.2s ease',

            // Move selected state here for better specificity
            '&.Mui-selected': {
              backgroundColor: YSBackgroundState.focus,
              color: YAMAN_ONYX,
              fontWeight: 600,
              borderColor: YSBorderState.focus,
              border: '1 0 1 0',
              '&:hover': {
                backgroundColor: YSBackgroundState.focusedHover,
              },
            },
          },
        },
      },

      // Slider
      MuiSlider: {
        styleOverrides: {
          root: {
            height: 2,
            padding: '15px 0',
          },
          rail: {
            backgroundColor: YSBorderState.default,
            opacity: 1,
          },
          track: {
            backgroundColor: YSBorderState.focus,
            border: 'none',
          },
          thumb: {
            height: 14,
            width: 14,
            backgroundColor: YSBorderState.focus,
            borderRadius: YSThemeStyleRadius.SHARP,
            border: `1px solid ${YSBorderState.focus}`,
            transition: 'all 0.2s ease',
            '&:hover': {
              // Tight, technical glow instead of a soft ripple
              boxShadow: `0 0 0 6px ${alpha(YAMAN_GOLD, 0.12)}`,
              backgroundColor: YAMAN_GOLD_DARK,
            },
            '&.Mui-active': {
              // "Pressed" state feedback
              boxShadow: `0 0 0 10px ${alpha(YAMAN_GOLD, 0.18)}`,
            },
          },
          mark: {
            width: 1,
            height: 4,
            backgroundColor: YSBorderState.hover,
          },
          markLabel: {
            lineHeight: 1,
            fontSize: 12,
            letterSpacing: '0.1em',
            color: YSLabelState.default,
            '&[data-index="0"]': {
              transform: 'none', // Stops the default centering
              textAlign: 'left',
            },
            '&.MuiSlider-markLabel[style*="left: 100%"], &:last-child': {
              transform: 'translateX(-100%) !important', // Pulls the label back so it doesn't overflow the grid
              textAlign: 'right',
            },
          },
          valueLabel: {
            fontSize: 12,
            fontWeight: 700, // Makes the number "pop" like a digital gauge
            lineHeight: 1.2,
            letterSpacing: '0.05em', // Technical spacing
            backgroundColor: YSBorderState.default,
            color: PURE_WHITE,
            borderRadius: YSThemeStyleRadius.SHARP, // Match the switch/thumb (2px)
            padding: '4px 8px', // Slightly wider for a "Tag" look
            // Remove the default "balloon" transformation for a static "Milled" look
            '&:before': {
              display: 'none', // Optional: removes the arrow for a very modern "floating tag"
            },
          },
        },
      },

      // Icons
      MuiInputAdornment: iconStyle,

      // Auto-Complete
      MuiAutocomplete: {
        styleOverrides: {
          root: {
            '& .MuiOutlinedInput-root': {
              height: 45,
              padding: '0 12px !important',
              borderRadius: YSThemeStyleRadius.SHARP,
              backgroundColor: YSBackgroundState.default,
            },
          },
          // Styles the search results dropdown
          paper: {
            borderRadius: YSThemeStyleRadius.SHARP,
            marginTop: 0,
            borderWidth: 1,
            borderColor: YSBorderState.default,
            boxShadow: CONTEXT_MENU_SHADOW,
            maxHeight: '320px !important',
            overflowY: 'auto',
          },
          noOptions: {
            fontSize: 13,
            fontWeight: 500,
            color: YSLabelState.default,
          },
          listbox: {
            padding: 0, // "Machined" look
            '& .MuiAutocomplete-option': {
              fontSize: 13,
              padding: '6px 10px',
              color: YSLabelState.default,
              // Match the background-color transitions from MuiMenuItem
              '&:hover': {
                backgroundColor: YSBackgroundState.hover,
              },
              '&[aria-selected="true"]': {
                backgroundColor: YSBackgroundState.focus, // Yaman Gold alpha
                fontWeight: 600,
                '&:hover': {
                  backgroundColor: YSBackgroundState.focusedHover,
                },
              },
            },
          },
          // The "Ignition" color for the clear/dropdown icons
          endAdornment: {
            right: '12px !important',
            '& .MuiSvgIcon-root': {
              fontSize: 20, // Match MuiSelect icon size
              color: YSIconState.default,
              '&:hover': {
                color: YSIconState.hover,
              },
              '.Mui-focused &': {
                color: YSIconState.focus,
              },
              '&.Mui-disabled': {
                color: YSIconState.disabled,
              },
            },
          },
        },
      },

      // Form Date/Time/Clock Picker
      MuiCalendarOrClockPicker: {
        styleOverrides: {
          root: {
            // Force a consistent machined width
            width: '320px !important',
            backgroundColor: PURE_WHITE,
            // Removes internal padding that causes the horizontal shift
            '& .css-epd502': {
              padding: 0,
              display: 'flex',
              justifyContent: 'center',
            },
          },
        },
      },
      MuiDateTimePicker: {
        defaultProps: {
          PopperProps: {
            placement: 'bottom-start',
          },
        },
      },
      MuiDatePicker: {
        defaultProps: {
          PopperProps: {
            placement: 'bottom-start',
          },
        },
      },
      MuiTimePicker: {
        defaultProps: {
          PopperProps: {
            placement: 'bottom-start',
          },
        },
      },
      MuiPickersPopper: {
        styleOverrides: {
          root: {
            marginTop: '3px !important',
          },
          paper: {
            // Align with the left edge of the TextField
            marginLeft: 0,
            borderRadius: YSThemeStyleRadius.SHARP,
            boxShadow: CONTEXT_MENU_SHADOW,
            margin: 0,
          },
        },
      },
      MuiClockPicker: {
        styleOverrides: {
          root: {
            backgroundColor: PURE_WHITE,
            '& .MuiTypography-root': {
              // This fixes the disappearing text by forcing Onyx color
              color: `${YAMAN_ONYX} !important`,
              fontSize: 12,
              textTransform: 'uppercase',
              letterSpacing: '0.05em',
            },
          },
          arrowSwitcher: {
            backgroundColor: PURE_WHITE,
          },
        },
      },
      // Target the Clock Dial itself to ensure the "Machined" look
      MuiClock: {
        styleOverrides: {
          clock: {
            backgroundColor: YSBackgroundState.default,
            border: `1px solid ${YSBorderState.default}`,
          },
          pin: {
            backgroundColor: YAMAN_GOLD,
          },
          amButton: {
            position: 'absolute',
            bottom: 12,
            left: 12,
          },
          pmButton: {
            position: 'absolute',
            bottom: 12,
            right: 12,
          },
        },
      },
      MuiClockPointer: {
        styleOverrides: {
          root: {
            backgroundColor: YAMAN_GOLD,
          },
          thumb: {
            borderColor: YAMAN_GOLD,
            color: PURE_WHITE,
          },
        },
      },
      MuiClockNumber: {
        styleOverrides: {
          root: {
            fontSize: 12, // Reduced for a more machined, technical look
            fontWeight: 500,
            '&.Mui-selected': {
              fontSize: 12, // Keep it consistent when selected
            },
          },
        },
      },
      MuiYearPicker: {
        styleOverrides: {
          root: {
            width: 320,
            backgroundColor: PURE_WHITE,
            // Target the private internal classes found in your HTML
            '& .PrivatePickersYear-root': {
              display: 'flex',
              justifyContent: 'center',
              alignItems: 'center',
            },
            '& .PrivatePickersYear-yearButton': {
              width: '72px !important',
              height: '36px !important',
              fontSize: '13px',
              borderRadius: `${YSThemeStyleRadius.SHARP}px !important`, // Sharp 2px
              color: YAMAN_ONYX,

              '&:hover': {
                backgroundColor: alpha(YAMAN_GOLD, 0.1),
              },

              '&.Mui-selected': {
                backgroundColor: `${YAMAN_GOLD} !important`,
                color: `${PURE_WHITE} !important`,
                fontWeight: 700,
                borderRadius: `${YSThemeStyleRadius.SHARP}px !important`, // Kill the circle
              },
            },
          },
        },
      },

      // Progress Bar:
      MuiLinearProgress: {
        styleOverrides: {
          root: {
            height: 2,
            backgroundColor: alpha(YAMAN_ONYX, 0.1),
            borderRadius: 0,
          },
          bar: {
            backgroundColor: 'primary.main', // The "Ignition" color
            borderRadius: 0, // No rounded caps for a machined look
          },
        },
      },
    },
  },
};

// --- Updated Sidebar to match Logic-Grid Cockpit ---
const LightSidebar = {
  sidebarBgColor: YAMAN_ONYX, // Dark Sidebar (Onyx) is more "Executive"
  sidebarTextColor: PURE_WHITE,
  sidebarHeaderColor: YAMAN_ONYX,
  sidebarMenuSelectedBgColor: YAMAN_GOLD, // Gold highlight for active item
  sidebarMenuSelectedTextColor: YAMAN_ONYX,
  sidebarMenuSelectedBarColor: YAMAN_GOLD_DARK,
  sidebarMenuHoverBgColor: alpha(YAMAN_GOLD, 0.08),
  sidebarMenuHoverTextColor: YAMAN_GOLD,
  mode: ThemeMode.LIGHT,
};

const YSConfig = {
  sidebar: {
    borderColor: '#2D2D2D',
    menuStyle: MenuStyle.STANDARD,
    isSidebarBgImage: false,
    sidebarBgImage: 1,
    colorSet: LightSidebar,
  },
  themeStyle: ThemeStyle.STANDARD,
  themeMode: ThemeMode.LIGHT, // App is Light (Pearl), Sidebar is Dark (Onyx)
  navStyle: NavStyle.UIWISE,
  layoutType: LayoutType.FULL_WIDTH,
  footerType: FooterType.FLUID,
  headerType: HeaderType.FIXED,
  rtAnim: RouteTransition.NONE,
  footer: false,
  locale: {
    languageId: 'english',
    locale: 'en',
    name: 'English',
    icon: 'us',
  },
  rtlLocale: ['ar'],
};

export default YSConfig;
